import { RecentlyViewed } from '@theme/recently-viewed-products';

class RecentlyViewedProducts extends HTMLElement {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    const productIds = this.#getFilteredProductIds();

    if (!productIds.length) {
      this.hidden = true;
      return;
    }

    if (this.dataset.searchPerformed === 'true') return;

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Returns recently viewed product IDs, excluding the current product if on a product page.
   * @returns {string[]}
   */
  #getFilteredProductIds() {
    const productIds = RecentlyViewed.getProducts();
    const currentProductId = this.dataset.currentProductId;

    if (currentProductId) {
      return productIds.filter((id) => id !== currentProductId);
    }

    return productIds;
  }

  async #loadProducts() {
    const productIds = this.#getFilteredProductIds();

    if (!productIds.length) {
      this.hidden = true;
      return;
    }

    const sectionId = this.dataset.sectionId;
    const id = this.id;

    if (!sectionId || !id) {
      throw new Error('Section ID and an ID attribute are required');
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', productIds.map((pid) => `id:${pid}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString(), { signal: this.#activeFetch.signal });

      if (!response.ok) {
        this.#handleError(new Error(`Server returned ${response.status}`));
        return;
      }

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const element = doc.querySelector(`recently-viewed-products[id="${id}"]`);

      if (element?.innerHTML?.trim().length) {
        this.dataset.searchPerformed = 'true';
        this.innerHTML = element.innerHTML;
        this.#reorderProducts(productIds);
      } else {
        this.hidden = true;
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        this.#handleError(e);
      }
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Reorders product slides/items to match recently-viewed order (most recent first).
   * @param {string[]} productIds
   */
  #reorderProducts(productIds) {
    const scroller = this.querySelector('slideshow-slides[ref="scroller"]');
    const container = scroller || this.querySelector('.resource-list--grid');
    if (!container) return;

    const items = scroller
      ? [...container.querySelectorAll(':scope > slideshow-slide')]
      : [...container.querySelectorAll(':scope > .resource-list__item')];

    const ordered = [];
    for (const id of productIds) {
      const item = items.find((el) => el.querySelector(`product-card[data-product-id="${id}"]`));
      if (item) ordered.push(item);
    }
    for (const item of items) {
      if (!ordered.includes(item)) ordered.push(item);
    }
    for (const item of ordered) {
      container.appendChild(item);
    }
  }

  /** @param {Error} error */
  #handleError(error) {
    console.error('Recently viewed products error:', error.message);
    this.hidden = true;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
